export type QuoteDiscountType = 'percent' | 'absolute';
export type QuoteDiscountApplicationMode = 'total' | 'distributed';

export interface PersistedQuoteEditorState {
  discountAmount: number;
  discountType: QuoteDiscountType;
  discountApplicationMode: QuoteDiscountApplicationMode;
  originalPrices: Record<number, number>;
  isDiscountAppliedToPrices: boolean;
}

export const DEFAULT_PERSISTED_QUOTE_EDITOR_STATE: PersistedQuoteEditorState = {
  discountAmount: 0,
  discountType: 'percent',
  discountApplicationMode: 'total',
  originalPrices: {},
  isDiscountAppliedToPrices: false,
};

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function normalizePersistedQuoteEditorState(
  input?: Partial<PersistedQuoteEditorState> | null,
): PersistedQuoteEditorState {
  const discountAmountRaw =
    typeof input?.discountAmount === 'number'
      ? input.discountAmount
      : Number(input?.discountAmount ?? DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountAmount);

  const discountAmount = Number.isFinite(discountAmountRaw)
    ? Math.max(0, roundCurrency(discountAmountRaw))
    : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountAmount;

  const discountType: QuoteDiscountType =
    input?.discountType === 'absolute' ? 'absolute' : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountType;

  const discountApplicationMode: QuoteDiscountApplicationMode =
    input?.discountApplicationMode === 'distributed'
      ? 'distributed'
      : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountApplicationMode;

  const rawOriginalPrices =
    input?.originalPrices && typeof input.originalPrices === 'object'
      ? input.originalPrices
      : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.originalPrices;

  const originalPrices: Record<number, number> = {};
  for (const [rawKey, rawValue] of Object.entries(rawOriginalPrices)) {
    const key = Number(rawKey);
    const value = Number(rawValue);

    if (!Number.isFinite(key) || !Number.isFinite(value)) {
      continue;
    }

    originalPrices[key] = Math.max(0, roundCurrency(value));
  }

  return {
    discountAmount,
    discountType,
    discountApplicationMode,
    originalPrices,
    isDiscountAppliedToPrices: Boolean(input?.isDiscountAppliedToPrices),
  };
}
